import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, ArrowUpCircle, MinusCircle, ArrowDownCircle, Users, User } from 'lucide-react';

const CreateTaskDialog = ({ open, onOpenChange, task, setTask, circles, members, selectedCircleId, onCircleChange, onSubmit }) => {
  const [step, setStep] = useState('form'); // 'form' | 'squad'

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!task.title?.trim()) return;
    setStep('squad');
  };

  const handleFinalSubmit = (needsSquad) => {
    setTask({ ...task, needs_squad: needsSquad });
    // Create a synthetic event and call onSubmit with the needs_squad value baked in
    const fakeEvent = { preventDefault: () => {} };
    // We need to pass the needs_squad through, so set it and let parent handle
    task.needs_squad = needsSquad;
    onSubmit(fakeEvent);
    setStep('form');
  };

  const handleOpenChange = (val) => {
    if (!val) setStep('form');
    onOpenChange(val);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button className="btn-primary h-9 text-sm" data-testid="add-task-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add Task
        </Button>
      </DialogTrigger>
      <DialogContent className="surface-card border-0 shadow-xl max-h-[90vh] overflow-y-auto">
        {step === 'form' ? (
          <>
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Add Task</DialogTitle>
              <DialogDescription>Add something to your to-do list.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleFormSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Circle</Label>
                <Select value={selectedCircleId} onValueChange={onCircleChange}>
                  <SelectTrigger data-testid="task-circle-select" className="rounded-xl"><SelectValue placeholder="Select circle" /></SelectTrigger>
                  <SelectContent>{circles.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>What needs to get done?</Label>
                <Input data-testid="task-title-input" placeholder="e.g., Pick up medication from CVS" value={task.title}
                  onChange={(e) => setTask({ ...task, title: e.target.value })} className="rounded-xl" required />
              </div>
              <div className="space-y-2">
                <Label>Details (optional)</Label>
                <Textarea data-testid="task-description-input" placeholder="Any additional info..." value={task.description}
                  onChange={(e) => setTask({ ...task, description: e.target.value })} className="resize-none rounded-xl" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select value={task.priority} onValueChange={(val) => setTask({ ...task, priority: val })}>
                    <SelectTrigger data-testid="task-priority-select" className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high"><span className="flex items-center gap-2"><ArrowUpCircle className="w-3.5 h-3.5 text-rose-500" /> High</span></SelectItem>
                      <SelectItem value="medium"><span className="flex items-center gap-2"><MinusCircle className="w-3.5 h-3.5 text-amber-500" /> Medium</span></SelectItem>
                      <SelectItem value="low"><span className="flex items-center gap-2"><ArrowDownCircle className="w-3.5 h-3.5 text-sky-500" /> Low</span></SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Due date</Label>
                  <Input type="date" data-testid="task-due-date-input" value={task.due_date}
                    onChange={(e) => setTask({ ...task, due_date: e.target.value })} className="rounded-xl" />
                </div>
              </div>
              <Button type="submit" className="w-full btn-primary" data-testid="next-step-btn">Next</Button>
            </form>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Do you need squad support on this?</DialogTitle>
              <DialogDescription>If yes, this will go on your squad{"'"}s pages so they can claim it.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 pt-2">
              <button
                onClick={() => handleFinalSubmit(true)}
                className="w-full flex items-center gap-3 p-4 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-cyan-300 dark:hover:border-cyan-700 transition-colors text-left group"
                data-testid="squad-yes-btn"
              >
                <div className="w-10 h-10 rounded-lg bg-cyan-50 dark:bg-cyan-900/20 flex items-center justify-center flex-shrink-0 group-hover:bg-cyan-100 transition-colors">
                  <Users className="w-5 h-5 text-cyan-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">Yes, share with my squad</p>
                  <p className="text-xs text-muted-foreground">Squad members can see and claim this task</p>
                </div>
              </button>
              <button
                onClick={() => handleFinalSubmit(false)}
                className="w-full flex items-center gap-3 p-4 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 transition-colors text-left group"
                data-testid="squad-no-btn"
              >
                <div className="w-10 h-10 rounded-lg bg-slate-50 dark:bg-slate-800 flex items-center justify-center flex-shrink-0 group-hover:bg-slate-100 transition-colors">
                  <User className="w-5 h-5 text-slate-500" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">No, just my personal to-do</p>
                  <p className="text-xs text-muted-foreground">Only you will see this task</p>
                </div>
              </button>
              <button
                onClick={() => setStep('form')}
                className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors text-center pt-1"
              >
                Back
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CreateTaskDialog;
